import { redirectToSignIn } from "./navigation";

const REQUEST_TIMEOUT_MS = 15_000;

export class ApiError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

interface RequestConfig {
  // When true, a 401 does not trigger the global sign-in redirect. For calls
  // where a 401 just means "no active session yet" (e.g. background token
  // refresh), not an expired session that should kick the user out.
  skipAuthRedirect?: boolean;
}

async function request<T>(url: string, options?: RequestInit, config?: RequestConfig): Promise<T> {
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT_MS);

  let res: Response;

  try {
    res = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options?.headers ?? {}),
      },
      credentials: "include",
      signal: timeoutController.signal,
    });
  } catch (err: any) {
    if (err?.name === "AbortError") {
      throw new ApiError("TIMEOUT", "Tempo esgotado, tente novamente");
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    if (res.status === 401 && !config?.skipAuthRedirect) {
      redirectToSignIn();
    }
    throw new ApiError(data.code ?? "UNKNOWN_ERROR", data.message ?? "Request failed");
  }

  return data as T;
}

export const http = {
  get: <T>(url: string) => request<T>(url),
  post: <T>(url: string, body: unknown) =>
    request<T>(url, { method: "POST", body: JSON.stringify(body) }),
  patch: <T>(url: string, body: unknown) =>
    request<T>(url, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(url: string) => request<T>(url, { method: "DELETE" }),
  postSilentAuth: <T>(url: string, body: unknown) =>
    request<T>(url, { method: "POST", body: JSON.stringify(body) }, { skipAuthRedirect: true }),
  getSilentAuth: <T>(url: string) => request<T>(url, undefined, { skipAuthRedirect: true }),
};
